import { useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEmailProvider } from './useEmailProvider';
import { useToast } from './use-toast';
import { useWorkspaces } from './useWorkspaces';
import { queryKeys } from '@/lib/query-keys';
import { supabase } from '@/lib/supabase';
import { safeBase64Encode } from '@/lib/email/utils';
import { EmailProvider } from '@/lib/email/types';
import { extractInlineImages, hasInlineImages, InlineImage } from '@/lib/email/inline-images';
import { normalizeEmailParagraphs } from '@/lib/email/normalize-paragraphs';
import { getContactIdByEmail } from '@/lib/email/contact-utils';
import { outlookService } from '@/lib/email/outlook';
import { captureError } from '@/lib/sentry';

// Type definitions

export interface EmailAttachment {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
}

export interface SendEmailParams {
  providerId: string;
  to: string | string[];
  cc?: string | string[];
  bcc?: string | string[];
  subject: string;
  body: string;
  signature?: string;
  attachments?: EmailAttachment[];
  /**
   * When true, this is a one-off "Send Test Email" (Phase 1 test-send feature).
   * The provider send still happens normally, but the post-send CRM logging
   * (sent_emails row + contact activity) is SKIPPED so test sends leave no
   * footprint. Existing callers omit this flag and are unaffected.
   */
  isTest?: boolean;
  contactId?: string;
}

// Size limits
const GMAIL_MAX_SIZE = 25 * 1024 * 1024; // 25MB
const OUTLOOK_DIRECT_MAX = 3 * 1024 * 1024; // 3MB per file for direct upload

// Helper: Convert File to Base64
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data:type;base64, prefix
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Build Gmail MIME message with attachments and inline images
async function buildGmailMimeMessage(
  provider: EmailProvider,
  params: SendEmailParams
): Promise<string> {
  const toList = Array.isArray(params.to) ? params.to : [params.to];
  const ccList = params.cc ? (Array.isArray(params.cc) ? params.cc : [params.cc]) : [];
  const bccList = params.bcc ? (Array.isArray(params.bcc) ? params.bcc : [params.bcc]) : [];

  const hasAttachments = params.attachments && params.attachments.length > 0;
  const hasImages = hasInlineImages(params.body);

  // Normalize paragraph spacing so Gmail renders like the composer/preview and
  // Outlook (which already gets this transform). Without it, Gmail applies its
  // default ~1em <p> margin and opens a blank line between every paragraph that
  // the sender never saw. See src/lib/email/normalize-paragraphs.ts.
  const normalizedBody = normalizeEmailParagraphs(params.body);

  // Extract inline images and replace with CID references
  let processedBody = normalizedBody;
  let inlineImages: InlineImage[] = [];
  if (hasImages) {
    const extracted = extractInlineImages(normalizedBody);
    processedBody = extracted.processedHtml;
    inlineImages = extracted.images;
  }

  // Simple message - no attachments, no inline images
  if (!hasAttachments && !hasImages) {
    return [
      'MIME-Version: 1.0',
      'Content-Type: text/html; charset=utf-8',
      `From: "${provider.display_name || provider.email}" <${provider.email}>`,
      `To: ${toList.join(', ')}`,
      ...(ccList.length ? [`Cc: ${ccList.join(', ')}`] : []),
      ...(bccList.length ? [`Bcc: ${bccList.join(', ')}`] : []),
      `Subject: ${params.subject}`,
      '',
      normalizedBody
    ].join('\r\n');
  }

  // Just inline images, no attachments - use multipart/related
  if (!hasAttachments && hasImages) {
    const relatedBoundary = `related_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const parts: string[] = [];

    parts.push(`From: "${provider.display_name || provider.email}" <${provider.email}>`);
    parts.push(`To: ${toList.join(', ')}`);
    if (ccList.length) parts.push(`Cc: ${ccList.join(', ')}`);
    if (bccList.length) parts.push(`Bcc: ${bccList.join(', ')}`);
    parts.push(`Subject: ${params.subject}`);
    parts.push('MIME-Version: 1.0');
    parts.push(`Content-Type: multipart/related; boundary="${relatedBoundary}"`);
    parts.push('');

    // HTML body
    parts.push(`--${relatedBoundary}`);
    parts.push('Content-Type: text/html; charset=utf-8');
    parts.push('Content-Transfer-Encoding: 7bit');
    parts.push('');
    parts.push(processedBody);
    parts.push('');

    // Inline images
    for (const image of inlineImages) {
      parts.push(`--${relatedBoundary}`);
      parts.push(`Content-Type: ${image.mimeType}`);
      parts.push(`Content-ID: <${image.cid}>`);
      parts.push('Content-Transfer-Encoding: base64');
      parts.push(`Content-Disposition: inline; filename="${image.filename}"`);
      parts.push('');
      parts.push(image.base64Data);
      parts.push('');
    }

    parts.push(`--${relatedBoundary}--`);
    return parts.join('\r\n');
  }

  // Attachments (with or without inline images) - use multipart/mixed
  const mixedBoundary = `mixed_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const parts: string[] = [];

  parts.push(`From: "${provider.display_name || provider.email}" <${provider.email}>`);
  parts.push(`To: ${toList.join(', ')}`);
  if (ccList.length) parts.push(`Cc: ${ccList.join(', ')}`);
  if (bccList.length) parts.push(`Bcc: ${bccList.join(', ')}`);
  parts.push(`Subject: ${params.subject}`);
  parts.push('MIME-Version: 1.0');
  parts.push(`Content-Type: multipart/mixed; boundary="${mixedBoundary}"`);
  parts.push('');

  if (hasImages) {
    // Nested multipart/related for HTML + inline images
    const relatedBoundary = `related_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    parts.push(`--${mixedBoundary}`);
    parts.push(`Content-Type: multipart/related; boundary="${relatedBoundary}"`);
    parts.push('');

    // HTML body
    parts.push(`--${relatedBoundary}`);
    parts.push('Content-Type: text/html; charset=utf-8');
    parts.push('Content-Transfer-Encoding: 7bit');
    parts.push('');
    parts.push(processedBody);
    parts.push('');

    // Inline images
    for (const image of inlineImages) {
      parts.push(`--${relatedBoundary}`);
      parts.push(`Content-Type: ${image.mimeType}`);
      parts.push(`Content-ID: <${image.cid}>`);
      parts.push('Content-Transfer-Encoding: base64');
      parts.push(`Content-Disposition: inline; filename="${image.filename}"`);
      parts.push('');
      parts.push(image.base64Data);
      parts.push('');
    }

    parts.push(`--${relatedBoundary}--`);
    parts.push('');
  } else {
    // Just HTML body (no inline images)
    parts.push(`--${mixedBoundary}`);
    parts.push('Content-Type: text/html; charset=utf-8');
    parts.push('Content-Transfer-Encoding: 7bit');
    parts.push('');
    parts.push(normalizedBody);
    parts.push('');
  }

  // Attachment parts
  for (const attachment of params.attachments!) {
    const base64Content = await fileToBase64(attachment.file);

    parts.push(`--${mixedBoundary}`);
    parts.push(`Content-Type: ${attachment.type}; name="${attachment.name}"`);
    parts.push(`Content-Disposition: attachment; filename="${attachment.name}"`);
    parts.push('Content-Transfer-Encoding: base64');
    parts.push('');
    parts.push(base64Content);
    parts.push('');
  }

  parts.push(`--${mixedBoundary}--`);
  return parts.join('\r\n');
}

// Server-side token refresh via edge function — works when MSAL cache is stale
async function refreshTokenServerSide(providerId: string): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/token-refresh`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ provider_id: providerId }),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Token refresh failed' }));
    throw new Error(error.error || 'Token refresh failed');
  }

  const data = await response.json();
  return data.access_token;
}

// Persist a client-acquired access token (e.g. from MSAL silent-refresh)
// through the same encrypted-write path used by server-side refresh. Calls
// token-refresh edge fn with `override_access_token` — the edge fn skips the
// OAuth round-trip and writes via email_provider_set_tokens.
async function persistAccessToken(
  providerId: string,
  accessToken: string,
  expiresOn: Date | null,
): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/token-refresh`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        provider_id: providerId,
        override_access_token: accessToken,
        override_expires_at: expiresOn ? expiresOn.toISOString() : null,
      }),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to persist access token');
  }
  return accessToken;
}

// Client-side Gmail OAuth refresh removed. Encrypted refresh_token is not
// readable by the client; refresh always goes through the server.
const refreshGmailToken = (provider: EmailProvider) => refreshTokenServerSide(provider.id);

// Refresh Outlook access token — try MSAL first, fall back to server-side refresh
async function refreshOutlookToken(provider: EmailProvider): Promise<string> {
  try {
    const { accessToken, expiresOn } = await outlookService.refreshAccessToken();
    // MSAL succeeded. Persist via the token-refresh edge fn override path —
    // the client cannot UPDATE encrypted bytea columns directly.
    return await persistAccessToken(provider.id, accessToken, expiresOn ?? null);
  } catch {
    console.log('MSAL silent refresh failed, trying server-side refresh...');
    return refreshTokenServerSide(provider.id);
  }
}

// Send email via Gmail with attachments
async function sendGmailEmail(
  provider: EmailProvider,
  params: SendEmailParams
): Promise<void> {
  // Validate total attachment size
  if (params.attachments?.length) {
    const totalSize = params.attachments.reduce((sum, a) => sum + a.size, 0);
    if (totalSize > GMAIL_MAX_SIZE) {
      throw new Error(`Total attachment size (${formatBytes(totalSize)}) exceeds Gmail's 25MB limit`);
    }
  }

  // EmailProvider no longer carries access_token (encrypted at rest). Always
  // fetch via the token-refresh edge fn which returns the cached token if
  // not expired, otherwise refreshes server-side.
  let accessToken: string;
  try {
    accessToken = await refreshGmailToken(provider);
  } catch (error) {
    console.error('Token refresh failed:', error);
    throw new Error('Email account needs to be reconnected');
  }

  const mimeMessage = await buildGmailMimeMessage(provider, params);

  const response = await fetch(`https://gmail.googleapis.com/gmail/v1/users/${provider.email}/messages/send`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ raw: safeBase64Encode(mimeMessage) })
  });

  if (!response.ok) {
    const error = await response.json();

    // Gmail returns 401 for expired/invalid tokens - try refresh once
    if (response.status === 401 || error.error?.code === 401) {
      try {
        accessToken = await refreshGmailToken(provider);

        // Retry with new token
        const retryResponse = await fetch(`https://gmail.googleapis.com/gmail/v1/users/${provider.email}/messages/send`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ raw: safeBase64Encode(mimeMessage) })
        });

        if (!retryResponse.ok) {
          const retryError = await retryResponse.json();
          throw new Error(retryError.error?.message || 'Failed to send email after token refresh');
        }

        return; // Success on retry
      } catch (refreshError) {
        console.error('Token refresh failed on 401:', refreshError);
        throw new Error('Email account needs to be reconnected');
      }
    }

    throw new Error(error.error?.message || 'Failed to send email');
  }
}

// Send email via Outlook with attachments and inline images
async function sendOutlookEmail(
  provider: EmailProvider,
  params: SendEmailParams
): Promise<void> {
  const toRecipients = (Array.isArray(params.to) ? params.to : [params.to])
    .map(email => ({ emailAddress: { address: email.trim() } }));
  const ccRecipients = params.cc
    ? (Array.isArray(params.cc) ? params.cc : [params.cc]).map(email => ({ emailAddress: { address: email.trim() } }))
    : [];
  const bccRecipients = params.bcc
    ? (Array.isArray(params.bcc) ? params.bcc : [params.bcc]).map(email => ({ emailAddress: { address: email.trim() } }))
    : [];

  const hasAttachments = params.attachments && params.attachments.length > 0;
  const hasImages = hasInlineImages(params.body);

  // Extract inline images if present
  let processedBody = params.body;
  let inlineImages: InlineImage[] = [];
  if (hasImages) {
    const extracted = extractInlineImages(params.body);
    processedBody = extracted.processedHtml;
    inlineImages = extracted.images;
  }

  // Preserve blank line spacing for Outlook:
  // 1. Reset paragraph margins to prevent CSS margin collapsing
  // 2. Fill empty paragraphs with &nbsp; so they render with line height
  const outlookBody = params.body
    .replace(/<p>/g, '<p style="margin:0;">')
    .replace(/<p style="margin:0;"><\/p>/g, '<p style="margin:0;">&nbsp;</p>')
    .replace(/<p style="margin:0;"><br\s*\/?><\/p>/g, '<p style="margin:0;">&nbsp;</p>');
  const outlookProcessedBody = processedBody
    .replace(/<p>/g, '<p style="margin:0;">')
    .replace(/<p style="margin:0;"><\/p>/g, '<p style="margin:0;">&nbsp;</p>')
    .replace(/<p style="margin:0;"><br\s*\/?><\/p>/g, '<p style="margin:0;">&nbsp;</p>');

  // EmailProvider no longer carries access_token. Refresh on every send;
  // MSAL silent-refresh is fast and cached.
  let accessToken: string;
  try {
    accessToken = await refreshOutlookToken(provider);
  } catch {
    throw new Error('No access token available. Please reconnect your email account.');
  }

  // Simple send without attachments or inline images
  if (!hasAttachments && !hasImages) {
    const response = await fetch('https://graph.microsoft.com/v1.0/me/sendMail', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        message: {
          subject: params.subject,
          body: {
            contentType: 'HTML',
            content: outlookBody,
          },
          toRecipients,
          ...(ccRecipients.length ? { ccRecipients } : {}),
          ...(bccRecipients.length ? { bccRecipients } : {})
        }
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      if (error.error?.code === 'InvalidAuthenticationToken') {
        // Attempt one refresh + retry before giving up
        try {
          accessToken = await refreshOutlookToken(provider);
          const retryResponse = await fetch('https://graph.microsoft.com/v1.0/me/sendMail', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              message: {
                subject: params.subject,
                body: {
                  contentType: 'HTML',
                  content: outlookBody,
                },
                toRecipients,
                ...(ccRecipients.length ? { ccRecipients } : {}),
                ...(bccRecipients.length ? { bccRecipients } : {})
              }
            })
          });
          if (!retryResponse.ok) {
            const retryError = await retryResponse.json().catch(() => ({}));
            throw new Error(retryError.error?.message || 'Failed to send email after token refresh');
          }
          return;
        } catch {
          throw new Error('Email account needs to be reconnected');
        }
      }
      throw new Error(error.error?.message || 'Failed to send email');
    }
    return;
  }

  // For emails with attachments or inline images, create a draft, add them, then send
  // Step 1: Create draft message
  const draftResponse = await fetch('https://graph.microsoft.com/v1.0/me/messages', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      subject: params.subject,
      body: {
        contentType: 'HTML',
        content: outlookProcessedBody,
      },
      toRecipients,
      ...(ccRecipients.length ? { ccRecipients } : {}),
      ...(bccRecipients.length ? { bccRecipients } : {})
    })
  });

  if (!draftResponse.ok) {
    const error = await draftResponse.json().catch(() => ({}));
    if (error.error?.code === 'InvalidAuthenticationToken') {
      try {
        accessToken = await refreshOutlookToken(provider);
      } catch {
        throw new Error('Email account needs to be reconnected');
      }
      // Retry draft creation with refreshed token
      const retryDraftResponse = await fetch('https://graph.microsoft.com/v1.0/me/messages', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject: params.subject,
          body: {
            contentType: 'HTML',
            content: outlookProcessedBody,
          },
          toRecipients,
          ...(ccRecipients.length ? { ccRecipients } : {}),
          ...(bccRecipients.length ? { bccRecipients } : {})
        })
      });
      if (!retryDraftResponse.ok) {
        const retryError = await retryDraftResponse.json().catch(() => ({}));
        throw new Error(retryError.error?.message || 'Failed to create draft message after token refresh');
      }
      const retryData = await retryDraftResponse.json();
      // Continue with the retry messageId below
      return await sendOutlookDraftWithAttachments(accessToken, retryData.id, inlineImages, params);
    }
    throw new Error(error.error?.message || 'Failed to create draft message');
  }

  const draftData = await draftResponse.json();
  await sendOutlookDraftWithAttachments(accessToken, draftData.id as string, inlineImages, params);
}

// Helper: add attachments to an Outlook draft and send it
async function sendOutlookDraftWithAttachments(
  accessToken: string,
  messageId: string,
  inlineImages: InlineImage[],
  params: SendEmailParams
): Promise<void> {
  // Helper to clean up draft on failure
  const cleanupDraft = async () => {
    await fetch(`https://graph.microsoft.com/v1.0/me/messages/${messageId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${accessToken}` }
    }).catch(() => {});
  };

  // Add inline images
  for (const image of inlineImages) {
    const attachResponse = await fetch(
      `https://graph.microsoft.com/v1.0/me/messages/${messageId}/attachments`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          '@odata.type': '#microsoft.graph.fileAttachment',
          name: image.filename,
          contentBytes: image.base64Data,
          contentType: image.mimeType,
          contentId: image.cid,
          isInline: true
        })
      }
    );

    if (!attachResponse.ok) {
      await cleanupDraft();
      const error = await attachResponse.json().catch(() => ({}));
      throw new Error(error.error?.message || `Failed to add inline image ${image.filename}`);
    }
  }

  // Add file attachments
  if (params.attachments) {
    for (const attachment of params.attachments) {
      if (attachment.size > OUTLOOK_DIRECT_MAX) {
        await cleanupDraft();
        throw new Error(`File "${attachment.name}" is larger than 3MB. Large file support coming soon.`);
      }

      const base64Content = await fileToBase64(attachment.file);

      const attachResponse = await fetch(
        `https://graph.microsoft.com/v1.0/me/messages/${messageId}/attachments`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            '@odata.type': '#microsoft.graph.fileAttachment',
            name: attachment.name,
            contentBytes: base64Content,
            contentType: attachment.type
          })
        }
      );

      if (!attachResponse.ok) {
        await cleanupDraft();
        const error = await attachResponse.json().catch(() => ({}));
        throw new Error(error.error?.message || `Failed to attach ${attachment.name}`);
      }
    }
  }

  // Send the message
  const sendResponse = await fetch(
    `https://graph.microsoft.com/v1.0/me/messages/${messageId}/send`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    }
  );

  if (!sendResponse.ok) {
    const error = await sendResponse.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to send message');
  }
}

// Format bytes to human readable
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function useEmailSender() {
  const [selectedProvider, setSelectedProvider] = useState<EmailProvider | null>(null);
  const initializationRef = useRef(false);
  const queryClient = useQueryClient();
  const { providers, loading } = useEmailProvider();
  const { toast } = useToast();
  const { currentWorkspace } = useWorkspaces();

  // Initialize with first provider when providers load
  useEffect(() => {
    if (!loading && providers.length > 0 && !initializationRef.current) {
      try {
        const initialProvider = providers[0];
        setSelectedProvider(initialProvider);
        initializationRef.current = true;
      } catch (error) {
        console.error('Error initializing provider:', error);
        captureError(error as Error, { feature: 'email-provider-init' });
        toast({
          title: "Error",
          description: "Failed to initialize email provider",
          variant: "destructive",
        });
      }
    }
  }, [providers, loading, toast]);

  const selectProvider = useCallback((providerId: string) => {
    try {
      const provider = providers.find(p => p.id === providerId);
      if (!provider) {
        throw new Error('Provider not found');
      }
      setSelectedProvider(provider);
    } catch (error) {
      console.error('Error selecting provider:', error);
      captureError(error as Error, { feature: 'email-provider-select' });
      toast({
        title: "Error",
        description: "Failed to select email provider",
        variant: "destructive",
      });
    }
  }, [providers, toast]);

  const sendEmail = useCallback(async (params: SendEmailParams): Promise<boolean> => {
    const { providerId, to, subject, body } = params;

    // Handle test account specially - bypass OAuth validation
    if (providerId === 'test-account' || providerId === 'test@builditcrm.com') {
      console.log('Test email send simulated:', { to, subject, body, attachments: params.attachments?.length });
      toast({
        title: "Test Email Sent",
        description: `Test email would be sent to: ${Array.isArray(to) ? to.join(', ') : to}`,
      });
      return true;
    }

    if (!selectedProvider) {
      throw new Error('No email provider selected');
    }

    try {
      const provider = providers.find(p => p.id === providerId);
      if (!provider) {
        throw new Error('Provider not found');
      }

      if (provider.type === 'gmail') {
        await sendGmailEmail(provider, params);
      } else if (provider.type === 'outlook') {
        await sendOutlookEmail(provider, params);
      }

      // Log sent email and create contact activity.
      // Skipped for one-off test sends (isTest) so they leave no CRM footprint.
      if (!params.isTest) try {
        const { data: { user } } = await supabase.auth.getUser();
        const recipientEmail = Array.isArray(params.to) ? params.to[0] : params.to;
        const contactId = params.contactId ?? await getContactIdByEmail(recipientEmail);

        // Strip HTML tags for preview
        const bodyPreview = params.body
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/g, ' ')
          .trim()
          .substring(0, 200);

        if (currentWorkspace && user) {
          // Get sender email from provider
          const provider = providers.find(p => p.id === params.providerId);

          // Insert sent email record
          await supabase.from('sent_emails').insert({
            workspace_id: currentWorkspace.id,
            user_id: user.id,
            provider_id: params.providerId,
            recipient_email: recipientEmail,
            contact_id: contactId,
            subject: params.subject,
            body: params.body,
            body_preview: bodyPreview,
            sender_email: provider?.email,
            sent_at: new Date().toISOString(),
            source: 'compose',
          });

          // Create activity on contact if found
          if (contactId) {
            await supabase.from('activities').insert({
              contact_id: contactId,
              workspace_id: currentWorkspace.id,
              user_id: user.id,
              type: 'email',
              title: params.subject || '(No subject)',
              description: params.body,
              start_date: new Date().toISOString(),
              scheduled: false,
              completed: false,
            });
            queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
          }
        }
      } catch (logError) {
        console.error('Failed to log sent email:', logError);
      }

      return true;
    } catch (error) {
      console.error('Error sending email:', error);
      if (error instanceof Error &&
          (error.message.includes('needs to be reconnected') ||
           error.message.includes('Token expired') || error.message.includes('401'))) {
        // Remove provider from database
        const { error: deleteError } = await supabase
          .from('email_providers')
          .delete()
          .eq('id', providerId);

        if (!deleteError) {
          if (selectedProvider?.id === providerId) {
            setSelectedProvider(null);
          }
        }
      }
      throw error;
    }
  }, [selectedProvider, providers, toast, currentWorkspace]);

  return {
    selectedProvider,
    selectProvider,
    providers,
    loading,
    sendEmail,
    initialized: initializationRef.current && providers.length > 0
  };
}
